import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaArrowRight, FaMapMarkerAlt, FaCalendarAlt } from 'react-icons/fa';
import { useSwipeable } from 'react-swipeable';
import Navbar from './Navbar';
import Footer from './Footer';
import { exhibitions } from './data/exhibitions';

const ExhibitionCard = ({ exhibition, currentImage, isTransitioning, isHovering, onNext, onPrev, onHover, onImageClick, onViewDetails }) => {
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => onNext(exhibition.id),
    onSwipedRight: () => onPrev(exhibition.id),
    preventDefaultTouchmoveEvent: true,
    trackMouse: true,
    delta: 10,
    swipeDuration: 500,
    touchEventOptions: { passive: false }
  });

  return (
    <div className="flex flex-col lg:flex-row gap-8 border border-gray-200 rounded-lg p-6 hover:shadow-lg transition duration-300">
      <div className="lg:w-1/2 relative">
        <div
          {...swipeHandlers}
          className="relative overflow-hidden rounded-lg aspect-video group touch-pan-y"
          onMouseEnter={() => onHover(exhibition.id, true)}
          onMouseLeave={() => onHover(exhibition.id, false)}
        >
          <div className="relative w-full h-full">
            {exhibition.images.map((image, index) => (
              <img
                key={index}
                src={image}
                alt={`${exhibition.title} - Image ${index + 1}`}
                className={`absolute top-0 left-0 w-full h-full object-cover transition-opacity duration-500 ${currentImage === index ? 'opacity-100' : 'opacity-0'}`}
                draggable="false"
              />
            ))}
          </div>
          <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <div
              className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-3 rounded-full hover:bg-black/70 transition-all duration-300 transform hover:scale-110 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                onPrev(exhibition.id);
              }}
            >
              <FaArrowLeft />
            </div>
            <div
              className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-3 rounded-full hover:bg-black/70 transition-all duration-300 transform hover:scale-110 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                onNext(exhibition.id);
              }}
            >
              <FaArrowRight />
            </div>
          </div>
        </div>
        <div className="flex justify-center mt-4 space-x-2">
          {exhibition.images.map((_, index) => (
            <div
              key={index}
              onClick={(e) => {
                e.stopPropagation();
                onImageClick(exhibition.id, index);
              }}
              className={`w-3 h-3 rounded-full transition-all duration-300 cursor-pointer ${currentImage === index ? 'bg-[#052e5a] w-6' : 'bg-gray-300 hover:bg-gray-400'}`}
            />
          ))}
        </div>
      </div>
      <div className="lg:w-1/2">
        <h2 className="text-2xl font-light mb-4" style={{ color: '#5e6a7d' }}>{exhibition.title}</h2>
        <div className="flex items-center gap-3 text-gray-600 mb-3">
          <FaMapMarkerAlt />
          <span className="font-light tracking-wide">{exhibition.location}</span>
        </div>
        <div className="flex items-center gap-3 text-gray-600 mb-4">
          <FaCalendarAlt />
          <span className="font-light tracking-wide">{exhibition.date}</span>
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-medium mb-2" style={{ color: '#052e5a' }}>Overview</h3>
          <p className="text-gray-600 font-light tracking-wide">{exhibition.overview}</p>
        </div>

        <div
          onClick={(e) => {
            e.stopPropagation();
            onViewDetails(exhibition);
          }}
          className="inline-block px-6 py-2 bg-[#052e5a] text-white rounded-full hover:bg-[#5e6a7d] transition-colors duration-300 font-light tracking-wide cursor-pointer"
        >
          View Details
        </div>
      </div>
    </div>
  );
};

const AnnualExhibitionCard = ({ exhibition, currentImage, isTransitioning, isHovering, onNext, onPrev, onHover, onImageClick }) => {
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => onNext(exhibition.id),
    onSwipedRight: () => onPrev(exhibition.id),
    preventDefaultTouchmoveEvent: true,
    trackMouse: true,
    delta: 10,
    swipeDuration: 500,
    touchEventOptions: { passive: false }
  });

  return (
    <div className="border border-gray-200 rounded-lg p-8 hover:shadow-lg transition duration-300">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-light mb-4" style={{ color: '#5e6a7d' }}>{exhibition.title}</h2>
          <div className="flex items-center justify-center gap-4 text-gray-600">
            <div className="flex items-center gap-2">
              <FaMapMarkerAlt />
              <span className="font-light tracking-wide">{exhibition.location}</span>
            </div>
            <div className="flex items-center gap-2">
              <FaCalendarAlt />
              <span className="font-light tracking-wide">{exhibition.date}</span>
            </div>
          </div>
        </div>

        <div className="relative mb-16">
          <div
            {...swipeHandlers}
            className="relative overflow-hidden rounded-lg aspect-video group touch-pan-y"
            onMouseEnter={() => onHover(exhibition.id, true)}
            onMouseLeave={() => onHover(exhibition.id, false)}
          >
            <div className="relative w-full h-full">
              {exhibition.images.map((image, index) => (
                <img
                  key={index}
                  src={image}
                  alt={`${exhibition.title} - Image ${index + 1}`}
                  className={`absolute top-0 left-0 w-full h-full object-cover transition-opacity duration-500 ${currentImage === index ? 'opacity-100' : 'opacity-0'}`}
                  draggable="false"
                />
              ))}
            </div>
            <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <div
                className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-3 rounded-full hover:bg-black/70 transition-all duration-300 transform hover:scale-110 cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  onPrev(exhibition.id);
                }}
              >
                <FaArrowLeft />
              </div>
              <div
                className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-3 rounded-full hover:bg-black/70 transition-all duration-300 transform hover:scale-110 cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  onNext(exhibition.id);
                }}
              >
                <FaArrowRight />
              </div>
            </div>
          </div>
          <div className="flex justify-center mt-4 space-x-2">
            {exhibition.images.map((_, index) => (
              <div
                key={index}
                onClick={(e) => {
                  e.stopPropagation();
                  onImageClick(exhibition.id, index);
                }}
                className={`w-3 h-3 rounded-full transition-all duration-300 cursor-pointer ${currentImage === index ? 'bg-[#052e5a] w-6' : 'bg-gray-300 hover:bg-gray-400'}`}
              />
            ))}
          </div>
        </div>

        <div className="grid gap-12">
          <section className="bg-white/50 p-6 rounded-lg">
            <h3 className="text-2xl font-medium mb-4" style={{ color: '#052e5a' }}>Overview</h3>
            <p className="text-gray-600 font-light tracking-wide leading-relaxed">{exhibition.overview}</p>
          </section>

          <section className="bg-white/50 p-6 rounded-lg">
            <h3 className="text-2xl font-medium mb-4" style={{ color: '#052e5a' }}>Purpose and Impact</h3>
            <p className="text-gray-600 font-light tracking-wide leading-relaxed">{exhibition.purposeAndImpact}</p>
          </section>

          <section className="bg-white/50 p-6 rounded-lg">
            <h3 className="text-2xl font-medium mb-4" style={{ color: '#052e5a' }}>Distinguished Guests</h3>
            <p className="text-gray-600 font-light tracking-wide leading-relaxed">{exhibition.distinguishedGuests}</p>
          </section>

          <section className="bg-white/50 p-6 rounded-lg">
            <h3 className="text-2xl font-medium mb-4" style={{ color: '#052e5a' }}>Audience and Media</h3>
            <p className="text-gray-600 font-light tracking-wide leading-relaxed">{exhibition.audience}</p>
          </section>

          <section className="bg-white/50 p-6 rounded-lg">
            <h3 className="text-2xl font-medium mb-4" style={{ color: '#052e5a' }}>Experience and Legacy</h3>
            <p className="text-gray-600 font-light tracking-wide leading-relaxed">{exhibition.mediaExperience}</p>
          </section>


        </div>
      </div>
    </div>
  );
};

const InternationalExhibitions = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('international');
  const [currentImages, setCurrentImages] = useState({});
  const [isTransitioning, setIsTransitioning] = useState({});
  const [isHovering, setIsHovering] = useState({});

  const handleViewDetails = (exhibition) => {
    navigate(`/exhibition/${activeTab}/${exhibition.id}`);
  };

  const nextImage = (exhibitionId) => {
    if (isTransitioning[exhibitionId]) return;
    const exhibition = exhibitions[activeTab].find(e => e.id === exhibitionId);
    if (!exhibition) return;

    setIsTransitioning(prev => ({ ...prev, [exhibitionId]: true }));
    setCurrentImages(prev => ({
      ...prev,
      [exhibitionId]: (prev[exhibitionId] + 1) % exhibition.images.length
    }));
    setTimeout(() => setIsTransitioning(prev => ({ ...prev, [exhibitionId]: false })), 500);
  };

  const prevImage = (exhibitionId) => {
    if (isTransitioning[exhibitionId]) return;
    const exhibition = exhibitions[activeTab].find(e => e.id === exhibitionId);
    if (!exhibition) return;

    setIsTransitioning(prev => ({ ...prev, [exhibitionId]: true }));
    setCurrentImages(prev => ({
      ...prev,
      [exhibitionId]: (prev[exhibitionId] - 1 + exhibition.images.length) % exhibition.images.length
    }));
    setTimeout(() => setIsTransitioning(prev => ({ ...prev, [exhibitionId]: false })), 500);
  };

  const handleImageClick = (exhibitionId, index) => {
    if (!isTransitioning[exhibitionId]) {
      setIsTransitioning(prev => ({ ...prev, [exhibitionId]: true }));
      setCurrentImages(prev => ({ ...prev, [exhibitionId]: index }));
      setTimeout(() => setIsTransitioning(prev => ({ ...prev, [exhibitionId]: false })), 500);
    }
  };

  const handleHover = (exhibitionId, isHovered) => {
    setIsHovering(prev => ({ ...prev, [exhibitionId]: isHovered }));
  };

  // Initialize current images when activeTab changes
  useEffect(() => {
    const initialImages = {};
    exhibitions[activeTab].forEach(exhibition => {
      initialImages[exhibition.id] = 0;
    });
    setCurrentImages(initialImages);
    setIsTransitioning({});
    setIsHovering({});
  }, [activeTab]);

  // Auto-scroll effect for each exhibition
  useEffect(() => {
    const intervals = {};
    exhibitions[activeTab].forEach(exhibition => {
      if (!isHovering[exhibition.id]) {
        intervals[exhibition.id] = setInterval(() => {
          nextImage(exhibition.id);
        }, 3000);
      }
    });

    return () => {
      Object.values(intervals).forEach(interval => clearInterval(interval));
    };
  }, [isHovering, activeTab]);

  return (
    <div className="min-h-screen bg-white text-gray-800 font-sans">
      <Navbar />

      <section className="pt-32 pb-16 px-4 bg-gradient-to-r from-[#052e5a]/10 to-[#5e6a7d]/10">
        <div className="container mx-auto">
          <h1 className="text-4xl sm:text-5xl font-light mb-4 tracking-wide text-center">
            <span className="font-normal" style={{ color: '#052e5a' }}>EXHIBITIONS</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto font-light tracking-wide text-center">
            Showcasing Egyptian talent on the global stage
          </p>
        </div>
      </section>

      <section className="py-12 px-4">
        <div className="container mx-auto">
          <div className="flex border-b border-gray-200 mb-8 overflow-x-auto">
            <div
              className={`flex-1 py-3 px-6 font-light tracking-wide uppercase cursor-pointer whitespace-nowrap text-center ${activeTab === 'international' ? 'border-b-2 border-[#052e5a] text-[#052e5a]' : 'text-gray-500'}`}
              onClick={() => setActiveTab('international')}
            >
              International Exhibitions
            </div>
            <div
              className={`flex-1 py-3 px-6 font-light tracking-wide uppercase cursor-pointer whitespace-nowrap text-center ${activeTab === 'annual' ? 'border-b-2 border-[#052e5a] text-[#052e5a]' : 'text-gray-500'}`}
              onClick={() => setActiveTab('annual')}
            >
              Annual Exhibitions
            </div>
          </div>

          <div className="space-y-16">
            {exhibitions[activeTab].map((exhibition) => (
              activeTab === 'international' ? (
                <ExhibitionCard
                  key={exhibition.id}
                  exhibition={exhibition}
                  currentImage={currentImages[exhibition.id]}
                  isTransitioning={isTransitioning[exhibition.id]}
                  isHovering={isHovering[exhibition.id]}
                  onNext={nextImage}
                  onPrev={prevImage}
                  onHover={handleHover}
                  onImageClick={handleImageClick}
                  onViewDetails={handleViewDetails}
                />
              ) : (
                <AnnualExhibitionCard
                  key={exhibition.id}
                  exhibition={exhibition}
                  currentImage={currentImages[exhibition.id]}
                  isTransitioning={isTransitioning[exhibition.id]}
                  isHovering={isHovering[exhibition.id]}
                  onNext={nextImage}
                  onPrev={prevImage}
                  onHover={handleHover}
                  onImageClick={handleImageClick}
                />
              )
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default InternationalExhibitions;